import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type Dispatch,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';

import { cx } from './internal/cx.js';

const VIEWPORT_PADDING = 8;

/**
 * Prefer right-aligning the menu to the trigger, then clamp into the viewport.
 */
function computeMenuCoords(
  triggerRect: DOMRect,
  menuWidth: number,
  menuHeight: number,
): { top: number; left: number; minWidth: number } {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const minWidth = Math.max(triggerRect.width, 10 * 16);

  let left = triggerRect.right - menuWidth;
  left = Math.max(VIEWPORT_PADDING, Math.min(left, viewportWidth - menuWidth - VIEWPORT_PADDING));

  let top = triggerRect.bottom + 4;
  if (menuHeight > 0 && top + menuHeight > viewportHeight - VIEWPORT_PADDING) {
    const above = triggerRect.top - menuHeight - 4;
    top = above >= VIEWPORT_PADDING ? above : VIEWPORT_PADDING;
  }

  return { top, left, minWidth };
}

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  menuId: string;
  highlightedIndex: number;
  setHighlightedIndex: Dispatch<SetStateAction<number>>;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);

function useDropdownMenuContext(): DropdownMenuContextValue {
  const ctx = useContext(DropdownMenuContext);
  if (!ctx) throw new Error('DropdownMenu components must be used within DropdownMenu.Root');
  return ctx;
}

function getEnabledMenuItems(content: HTMLElement | null): NodeListOf<HTMLElement> | undefined {
  return content?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)');
}

export interface DropdownMenuRootProps {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function DropdownMenuRoot({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
}: DropdownMenuRootProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const setOpen = useCallback(
    (next: boolean): void => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
      if (!next) setHighlightedIndex(-1);
    },
    [isControlled, onOpenChange],
  );

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: globalThis.MouseEvent): void {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || contentRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }

    function handleKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, setOpen]);

  return (
    <DropdownMenuContext.Provider
      value={{
        open,
        setOpen,
        triggerRef,
        contentRef,
        menuId,
        highlightedIndex,
        setHighlightedIndex,
      }}
    >
      <div className="nrx-dropdown-menu">{children}</div>
    </DropdownMenuContext.Provider>
  );
}

export interface DropdownMenuTriggerProps {
  children: ReactNode;
  className?: string;
  /** When true, merges trigger props into the single child element instead of wrapping in a button. */
  asChild?: boolean;
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>): (instance: T | null) => void {
  return (instance) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(instance);
      else if (ref != null) ref.current = instance;
    }
  };
}

function DropdownMenuTrigger({
  children,
  className,
  asChild = false,
}: DropdownMenuTriggerProps): JSX.Element {
  const { open, setOpen, triggerRef, menuId } = useDropdownMenuContext();

  const triggerProps = {
    className: cx('nrx-dropdown-menu__trigger', className),
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setOpen(!open);
    },
  };

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{
      className?: string;
      onClick?: (e: MouseEvent<HTMLElement>) => void;
      ref?: Ref<HTMLElement>;
    }>;
    return cloneElement(child, {
      ...triggerProps,
      className: cx(triggerProps.className, child.props.className),
      ref: mergeRefs(triggerRef, child.props.ref),
      onClick: (event: MouseEvent<HTMLElement>) => {
        child.props.onClick?.(event);
        if (!event.defaultPrevented) triggerProps.onClick(event);
      },
    });
  }

  return (
    <button ref={triggerRef} type="button" {...triggerProps}>
      {children}
    </button>
  );
}

export interface DropdownMenuContentProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

function DropdownMenuContent({
  children,
  className,
  ...props
}: DropdownMenuContentProps): JSX.Element | null {
  const { open, triggerRef, contentRef, menuId, highlightedIndex, setHighlightedIndex } =
    useDropdownMenuContext();
  const [coords, setCoords] = useState({ top: 0, left: 0, minWidth: 0 });

  const updatePosition = useCallback((): void => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const content = contentRef.current;
    const menuWidth = content?.offsetWidth || Math.max(rect.width, 160);
    const menuHeight = content?.offsetHeight || 0;
    setCoords(computeMenuCoords(rect, menuWidth, menuHeight));
  }, [triggerRef, contentRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, children, updatePosition]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    contentRef.current?.focus();
    const items = getEnabledMenuItems(contentRef.current);
    setHighlightedIndex(items && items.length > 0 ? 0 : -1);
  }, [open, contentRef, setHighlightedIndex]);

  useEffect(() => {
    if (!open) return;
    const items = getEnabledMenuItems(contentRef.current);
    const count = items?.length ?? 0;
    setHighlightedIndex((current) => {
      if (count === 0) return -1;
      if (current >= count) return count - 1;
      return current;
    });
  }, [open, children, contentRef, setHighlightedIndex]);

  const enabledItems = getEnabledMenuItems(contentRef.current);
  const activeDescendantId =
    highlightedIndex >= 0 && enabledItems != null && highlightedIndex < enabledItems.length
      ? enabledItems[highlightedIndex]?.id
      : undefined;

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    const items = getEnabledMenuItems(contentRef.current);
    if (!items || items.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = highlightedIndex < 0 ? 0 : (highlightedIndex + 1) % items.length;
      setHighlightedIndex(next);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next =
        highlightedIndex < 0
          ? items.length - 1
          : (highlightedIndex - 1 + items.length) % items.length;
      setHighlightedIndex(next);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setHighlightedIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setHighlightedIndex(items.length - 1);
    } else if (
      (event.key === 'Enter' || event.key === ' ') &&
      highlightedIndex >= 0 &&
      highlightedIndex < items.length
    ) {
      event.preventDefault();
      items[highlightedIndex]?.click();
    }
  }

  if (!open) return null;

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={contentRef}
      id={menuId}
      role="menu"
      aria-activedescendant={activeDescendantId}
      className={cx('nrx-dropdown-menu__content', className)}
      style={{ top: coords.top, left: coords.left, minWidth: coords.minWidth }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      {...props}
    >
      {children}
    </div>,
    document.body,
  );
}

export type DropdownMenuItemTone = 'default' | 'danger';

export interface DropdownMenuItemProps extends ComponentPropsWithoutRef<'button'> {
  children: ReactNode;
  /** Visual intent for menu actions. Use `danger` for irreversible operations. */
  tone?: DropdownMenuItemTone;
  onSelect?: () => void;
}

function DropdownMenuItem({
  children,
  className,
  tone = 'default',
  disabled,
  onSelect,
  onClick,
  ...props
}: DropdownMenuItemProps): JSX.Element {
  const { setOpen, contentRef, highlightedIndex, setHighlightedIndex } = useDropdownMenuContext();
  const itemId = useId();

  const enabledItems = getEnabledMenuItems(contentRef.current);
  const isHighlighted =
    !disabled &&
    highlightedIndex >= 0 &&
    enabledItems != null &&
    highlightedIndex < enabledItems.length &&
    enabledItems[highlightedIndex]?.id === itemId;

  function handleClick(event: MouseEvent<HTMLButtonElement>): void {
    onClick?.(event);
    if (!event.defaultPrevented) {
      onSelect?.();
      setOpen(false);
    }
  }

  function handleMouseEnter(event: MouseEvent<HTMLButtonElement>): void {
    if (disabled) return;
    const items = getEnabledMenuItems(contentRef.current);
    if (!items) return;
    const index = Array.from(items).indexOf(event.currentTarget);
    if (index >= 0) setHighlightedIndex(index);
  }

  return (
    <button
      type="button"
      role="menuitem"
      id={itemId}
      tabIndex={-1}
      disabled={disabled}
      className={cx(
        'nrx-dropdown-menu__item',
        tone === 'danger' && 'nrx-dropdown-menu__item--destructive',
        isHighlighted && 'nrx-dropdown-menu__item--highlighted',
        className,
      )}
      onMouseEnter={handleMouseEnter}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

export type DropdownMenuSeparatorProps = ComponentPropsWithoutRef<'div'>;

function DropdownMenuSeparator({ className, ...props }: DropdownMenuSeparatorProps): JSX.Element {
  return (
    <div
      role="separator"
      className={cx('nrx-dropdown-menu__separator', className)}
      aria-orientation="horizontal"
      {...props}
    />
  );
}

export interface DropdownMenuLabelProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

function DropdownMenuLabel({ children, className, ...props }: DropdownMenuLabelProps): JSX.Element {
  return (
    <div role="presentation" className={cx('nrx-dropdown-menu__label', className)} {...props}>
      {children}
    </div>
  );
}

/** Menu representing a set of actions, triggered by a button. */
export const DropdownMenu = {
  Root: DropdownMenuRoot,
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator,
  Label: DropdownMenuLabel,
};
